import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { UserProvider, useUser } from './components/providers/UserProvider';
import { GunProvider } from './components/providers/GunProvider';
import MiniKitProvider from './components/providers/MiniKitProvider';
import { ToastProvider, useToast } from './hooks/useToast';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { OfflineBanner } from './components/ui/OfflineBanner';
import { ConfirmDialog } from './components/ui/ConfirmDialog';
import { MapView } from './components/map/MapView';
import { MapFilterToggle, MapFilters } from './components/map/MapFilterToggle';
import { BottomNav, TabName } from './components/ui/BottomNav';
import { BottomSheet } from './components/ui/BottomSheet';
import { ToastRenderer } from './components/ui/Toast';
import { Onboarding } from './components/ui/Onboarding';
import { ActivityPanel } from './components/activity/ActivityPanel';
import { ActivitySuggestionCard } from './components/activity/ActivitySuggestion';
import { ProfilePanel } from './components/user/ProfilePanel';
import { SettingsPanel } from './components/user/SettingsPanel';
import { BlockReportSheet } from './components/user/BlockReportSheet';
import { useNotifUnreadCount } from './hooks/useNotifUnreadCount';
import { useFollows } from './hooks/useFollows';
// usePushSubscription intentionally not called inside MiniKit/WorldApp — Notification API unavailable there
import { StabeonUser, ACTIVITY_CATEGORIES } from './types/user';
import { Pin } from './types/pin';
import { StabeonEvent } from './types/event';
import { ChatTarget, ChatGroup } from './types/chat';
import { useLocation, LocationPrecision } from './hooks/useLocation';
import { useReverseGeocode } from './hooks/useReverseGeocode';
import { useActivityTracker } from './hooks/useActivityTracker';
import { useDiary } from './hooks/useDiary';
import { useReferral } from './hooks/useReferral';
import { usePins } from './hooks/usePins';
import { useCapsules } from './hooks/useCapsules';
import { useVideoCall } from './hooks/useVideoCall';
import { useNotifications } from './hooks/useNotifications';
import { useDeepLinks } from './hooks/useDeepLinks';
import { useBlockList } from './hooks/useBlockList';
import { useConversations } from './hooks/useConversations';
import { useRateLimit } from './hooks/useRateLimit';
import { useTheme } from './hooks/useTheme';
import { useNotifPrefs } from './hooks/useNotifPrefs';
import { useUsers } from './hooks/useUsers';
import { useEvents } from './hooks/useEvents';
import { useEventReminders } from './hooks/useEventReminders';
import { SearchBar } from './components/ui/SearchBar';
import { AvatarDisplay } from './components/avatar/AvatarDisplay';
import { AdminDashboard, ADMIN_HASH } from './components/admin/AdminDashboard';
import { useAnalytics } from './hooks/useAnalytics';
import { identifyUser, resetAnalytics, trackScreen, track } from './analytics';
import gun from './config/gun.config';
import './App.css';

// Code-split heavy components — loaded only when their tab/sheet is first opened
const ChatList         = React.lazy(() => import('./components/chat/ChatList').then(m => ({ default: m.ChatList })));
const ChatWindow       = React.lazy(() => import('./components/chat/ChatWindow').then(m => ({ default: m.ChatWindow })));
const AgentWindow      = React.lazy(() => import('./components/chat/AgentWindow').then(m => ({ default: m.AgentWindow })));
const CreateGroupSheet = React.lazy(() => import('./components/chat/CreateGroupSheet').then(m => ({ default: m.CreateGroupSheet })));
const GroupInfoSheet   = React.lazy(() => import('./components/chat/GroupInfoSheet').then(m => ({ default: m.GroupInfoSheet })));
const VideoCallOverlay = React.lazy(() => import('./components/chat/VideoCallOverlay').then(m => ({ default: m.VideoCallOverlay })));
const CreatePinSheet      = React.lazy(() => import('./components/pins/CreatePinSheet').then(m => ({ default: m.CreatePinSheet })));
const CreateCapsuleSheet  = React.lazy(() => import('./components/capsules/CreateCapsuleSheet').then(m => ({ default: m.CreateCapsuleSheet })));
const CapsuleRevealSheet  = React.lazy(() => import('./components/capsules/CapsuleRevealSheet').then(m => ({ default: m.CapsuleRevealSheet })));
const PinDetailSheet   = React.lazy(() => import('./components/pins/PinDetailSheet').then(m => ({ default: m.PinDetailSheet })));
const CreateEventSheet = React.lazy(() => import('./components/events/CreateEventSheet').then(m => ({ default: m.CreateEventSheet })));
const EventDetailSheet = React.lazy(() => import('./components/events/EventDetailSheet').then(m => ({ default: m.EventDetailSheet })));
const CalendarView     = React.lazy(() => import('./components/events/CalendarView').then(m => ({ default: m.CalendarView })));
const NotificationsPanel = React.lazy(() => import('./components/notifications/NotificationsPanel').then(m => ({ default: m.NotificationsPanel })));
const OpportunityTab     = React.lazy(() => import('./components/opportunities/OpportunityTab').then(m => ({ default: m.OpportunityTab })));
const MatchTab           = React.lazy(() => import('./components/match/MatchTab').then(m => ({ default: m.MatchTab })));

function AppInner() {
  const { theme, toggleTheme } = useTheme();
  const { prefs: notifPrefs, update: updateNotifPrefs, isEnabled: notifEnabled } = useNotifPrefs();
  const { user, updateUser, logout, previewMode, exitPreview } = useUser();
  const { show } = useToast();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { referralSource } = useReferral();
  const { pins } = usePins();
  const { capsules, addCapsule: _addCapsule, markRevealed, deleteCapsule: _deleteCapsule, unrevealedUnlocked } = useCapsules();
  const allUsers = useUsers();
  const { events: allEvents, pastEvents } = useEvents();
  const allEventsAndPast = useMemo(() => [...allEvents, ...pastEvents], [allEvents, pastEvents]);
  useEventReminders(allEvents, user?.nullifierHash || null, allUsers, notifEnabled('events'));
  const { trackEvent } = useAnalytics(user?.nullifierHash || null);

  // ── PostHog: identify user on login / logout ─────────────────────────────
  useEffect(() => {
    if (user?.nullifierHash) {
      identifyUser(user.nullifierHash, user.nickname);
    } else {
      resetAnalytics();
    }
  }, [user?.nullifierHash, user?.nickname]);

  const [adminOpen, setAdminOpen] = useState(false);
  const isAdmin = user?.nullifierHash === ADMIN_HASH;
  const videoCall = useVideoCall(user?.nullifierHash || '', user?.nickname || '');
  const { notify, requestPermission } = useNotifications();
  const deepLink = useDeepLinks();
  const { isBlocked } = useBlockList(user?.nullifierHash || null);
  const notifCount = useNotifUnreadCount(user?.nullifierHash || null);
  const { following } = useFollows(user?.nullifierHash || null);
  // Web Push subscription skipped — use MiniKit notification system (useNotifications) instead
  const { convos, record: recordConvo, markRead: markConvoRead, markAllRead: markAllConvosRead, updatePreview: updateConvoPreview } = useConversations(user?.nullifierHash || null);
  const unreadCount = useMemo(() => convos.reduce((sum, c) => sum + c.unread, 0), [convos]);
  const pinRateLimit = useRateLimit(10000); // 10s between pins
  const eventRateLimit = useRateLimit(2000); // minimal cooldown

  const [tab, setTab] = useState<TabName>('map');
  const [newMatchCount, setNewMatchCount] = useState(0);
  // Keep refs for stale-closure-safe access inside GUN listeners
  const tabRef = useRef<TabName>('map');
  useEffect(() => { tabRef.current = tab; }, [tab]);
  // PostHog: track screen view on every tab change, with time-on-previous-tab
  const tabEnteredAt = useRef<number>(Date.now());
  useEffect(() => {
    const now = Date.now();
    const timeOnPrev = Math.round((now - tabEnteredAt.current) / 1000);
    track('tab_switched', { tab, time_on_prev_tab_sec: timeOnPrev });
    tabEnteredAt.current = now;
    trackScreen(tab);
  }, [tab]);
  const notifEnabledRef = useRef(notifEnabled);
  useEffect(() => { notifEnabledRef.current = notifEnabled; }, [notifEnabled]);
  const [precision, setPrecision] = useState<LocationPrecision>(
    (user?.locationPrecision as LocationPrecision) || 'exact'
  );
  const [radius, setRadius] = useState<number | null>(user?.visibilityRadius ?? null);
  const [mapFilters, setMapFilters] = useState<MapFilters>({ showUsers: true, showPins: true, showEvents: true });

  // Sheets
  const [activityOpen, setActivityOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<StabeonUser | null>(null);
  const [blockReportOpen, setBlockReportOpen] = useState(false);
  const [blockReportUser, setBlockReportUser] = useState<StabeonUser | null>(null);

  // Chat
  const [chatTarget, setChatTarget] = useState<ChatTarget | null>(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ChatGroup | null>(null);

  // Pins
  const [createPinOpen, setCreatePinOpen] = useState(false);
  const [createPinLocation, setCreatePinLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedPin, setSelectedPin] = useState<Pin | null>(null);
  const [pinDetailOpen, setPinDetailOpen] = useState(false);

  // Capsules
  const [createCapsuleOpen, setCreateCapsuleOpen] = useState(false);
  const [createCapsuleLocation, setCreateCapsuleLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [revealingCapsule, setRevealingCapsule] = useState<import('./types/capsule').CapsulePin | null>(null);
  const revealQueueRef = useRef<import('./types/capsule').CapsulePin[]>([]);

  // Events
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [createEventLocation, setCreateEventLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<StabeonEvent | null>(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);

  // Search
  const [searchOpen, setSearchOpen] = useState(false);
  const [flyToLocation, setFlyToLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Confirm dialogs
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  // Location
  const loc = useLocation(precision);
  useReverseGeocode(loc.lat, loc.lng);
  const { suggestion, dismiss } = useActivityTracker(loc.lat, loc.lng);
  const { entries: diary, addEntry } = useDiary(user?.nullifierHash || '');
  const [showOnboarding, setShowOnboarding] = useState(!user?.onboarded);

  // Request notification permission after onboarding
  useEffect(() => {
    if (user?.onboarded) requestPermission();
  }, [user?.onboarded, requestPermission]);

  // Auto-reveal unlocked capsules
  useEffect(() => {
    if (unrevealedUnlocked.length === 0) return;
    revealQueueRef.current = [...unrevealedUnlocked];
    if (!revealingCapsule) {
      setRevealingCapsule(revealQueueRef.current.shift() || null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unrevealedUnlocked.length]);

  // Handle deep links
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current || !deepLink.id || !user) return;
    deepLinkHandled.current = true;
    if (deepLink.type === 'pin') {
      const pin = pins.find(p => p.id === deepLink.id);
      if (pin) { setSelectedPin(pin); setPinDetailOpen(true); }
      else show('Pin not found or expired', 'error');
    } else if (deepLink.type === 'event') {
      const evt = Object.values(allEvents).find(e => e.id === deepLink.id);
      if (evt) { setSelectedEvent(evt); setEventDetailOpen(true); }
      else show('Event not found', 'error');
    } else if (deepLink.type === 'user') {
      const u = allUsers.find(u => u.nullifierHash === deepLink.id);
      if (u) { setSelectedUser(u); setProfileOpen(true); }
    }
  }, [deepLink, pins, allEvents, allUsers, user, show]);

  // Nearby pin notifications — track by ID to avoid spam
  const knownPinIds = useRef(new Set<string>());
  useEffect(() => {
    if (knownPinIds.current.size === 0) {
      // First load — just seed the set, don't notify
      pins.forEach(p => knownPinIds.current.add(p.id));
      return;
    }
    const newPins = pins.filter(p => !knownPinIds.current.has(p.id));
    if (newPins.length > 0) {
      newPins.forEach(p => knownPinIds.current.add(p.id));
      if (notifEnabled('nearbyPins')) {
        notify('Joystance', `${newPins.length} new pin${newPins.length > 1 ? 's' : ''} nearby!`);
        show(`${newPins.length} new pin${newPins.length > 1 ? 's' : ''} nearby`, 'info');
      }
    }
  }, [pins, notify, show]);

  // DM notification listener — uses tabRef/notifEnabledRef to avoid stale closures
  const dmNotifSeen = useRef(new Set<string>());
  useEffect(() => {
    if (!user) return;
    const node = gun.get('joystance_notifications').get(user.nullifierHash);
    const mapNode = node.map();
    mapNode.on((data: any, id: string) => {
      if (!data || !data.ts || !id || dmNotifSeen.current.has(id)) return;
      if (Date.now() - data.ts > 30000) return; // Only notify for messages < 30s old
      dmNotifSeen.current.add(id);
      if (tabRef.current !== 'chat' && notifEnabledRef.current('messages')) {
        notify('Joystance', `${data.fromNick}: ${data.text || 'sent a message'}`);
        show(`New message from ${data.fromNick}`, 'info');
      }
    });
    return () => { try { mapNode.off(); } catch {} };
  }, [user, notify, show]);

  useEffect(() => { if (tab === 'chat') markAllConvosRead(); }, [tab, markAllConvosRead]);

  useEffect(() => {
    if (chatTarget && tab === 'chat') {
      track('chat_opened', { chat_type: chatTarget.type });
    }
  }, [chatTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced location push
  const locationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!user) return;
    if (locationTimer.current) clearTimeout(locationTimer.current);
    locationTimer.current = setTimeout(() => {
      updateUser({ lat: loc.lat, lng: loc.lng, isMoving: loc.isMoving, heading: loc.heading, lastSeen: Date.now(), isOnline: true });
    }, 500);
    return () => { if (locationTimer.current) clearTimeout(locationTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.lat, loc.lng, loc.isMoving, loc.heading]);

  // Heartbeat — every 30s to stay in the 5min online window
  useEffect(() => {
    if (!user) return;
    // Immediate heartbeat on mount
    gun.get('stabeon_users').get(user.nullifierHash).get('lastSeen').put(Date.now());
    gun.get('stabeon_users').get(user.nullifierHash).get('isOnline').put(true);
    const id = setInterval(() => {
      gun.get('stabeon_users').get(user.nullifierHash).get('lastSeen').put(Date.now());
      gun.get('stabeon_users').get(user.nullifierHash).get('isOnline').put(true);
    }, 30_000);
    return () => clearInterval(id);
  }, [user]);

  const openCreatePin = useCallback((lat: number, lng: number) => {
    if (previewMode) { show('Sign in to drop a pin', 'info'); return; }
    trackEvent('pin_created');
    track('pin_create_opened');
    if (!pinRateLimit.canAct()) {
      show('Wait a few seconds before dropping another pin', 'error');
      return;
    }
    setCreatePinLocation({ lat, lng });
    setCreatePinOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinRateLimit.canAct, show, previewMode, trackEvent]);

  const openCreateCapsule = useCallback((lat: number, lng: number) => {
    if (previewMode) { show('Sign in to drop a time capsule', 'info'); return; }
    setCreateCapsuleLocation({ lat, lng });
    setCreateCapsuleOpen(true);
  }, [show, previewMode]);

  const openCreateEvent = useCallback((lat?: number, lng?: number) => {
    if (previewMode) { show('Sign in to create an event', 'info'); return; }
    trackEvent('event_created');
    track('event_create_opened');
    if (!eventRateLimit.canAct()) {
      show('Wait before creating another event', 'error');
      return;
    }
    setCreateEventLocation({ lat: lat || loc.lat || 0, lng: lng || loc.lng || 0 });
    setCreateEventOpen(true);
  }, [eventRateLimit, show, loc.lat, loc.lng, previewMode, trackEvent]);

  const handleVideoCall = () => {
    if (chatTarget?.type === 'dm') {
      videoCall.startCall(chatTarget.peerId, true, chatTarget.peerNick);
    }
  };

  const handleAudioCall = () => {
    if (chatTarget?.type === 'dm') {
      videoCall.startCall(chatTarget.peerId, false, chatTarget.peerNick);
    }
  };

  const openProfile = (u: StabeonUser) => {
    if (isBlocked(u.nullifierHash)) return;
    setSelectedUser(u);
    setProfileOpen(true);
  };

  const openBlockReport = (u: StabeonUser) => {
    setBlockReportUser(u);
    setBlockReportOpen(true);
    setProfileOpen(false);
  };

  if (showOnboarding && !previewMode) return (
    <Onboarding
      onComplete={(firstAction) => {
        setShowOnboarding(false);
        if (firstAction === 'chat') {
          setTab('chat');
        } else if (firstAction === 'pin') {
          setTab('map');
          setTimeout(() => setCreatePinOpen(true), 400);
        } else if (firstAction === 'event') {
          setTab('events');
          setTimeout(() => setCreateEventOpen(true), 400);
        } else {
          setTab('map');
        }
      }}
    />
  );

  return (
    <div className="w-screen flex flex-col bg-stabeon-bg" style={{ height: '100dvh', paddingTop: 'env(safe-area-inset-top, 0px)', overflowX: 'hidden', overflowY: 'hidden' }}>
      <OfflineBanner />
      {/* Location permission denied — persistent banner with settings link */}
      {loc.error && !loc.granted && (
        <div className="fixed left-0 right-0 z-[9997] flex items-center justify-between gap-2 px-4 py-2.5 bg-amber-600/95 backdrop-blur-sm" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
          <p className="text-white text-xs font-semibold leading-tight">
            📍 Location access needed to see what's nearby
          </p>
          <a
            href="app-settings:"
            className="text-white text-xs font-bold underline underline-offset-2 flex-shrink-0 opacity-90 hover:opacity-100"
          >
            Settings
          </a>
        </div>
      )}
      <ToastRenderer />

      <React.Suspense fallback={null}>
        <VideoCallOverlay
          callState={videoCall.callState}
          localStream={videoCall.localStream}
          remoteStream={videoCall.remoteStream}
          isMuted={videoCall.isMuted}
          isVideoOff={videoCall.isVideoOff}
          isSpeaker={videoCall.isSpeaker}
          callDuration={videoCall.callDuration}
          incomingCall={videoCall.incomingCall}
          callPeerNick={videoCall.callPeerNick}
          callError={videoCall.callError}
          onAnswer={videoCall.answerCall}
          onReject={videoCall.rejectCall}
          onEnd={videoCall.endCall}
          onToggleMute={videoCall.toggleMute}
          onToggleVideo={videoCall.toggleVideo}
          onToggleSpeaker={videoCall.toggleSpeaker}
          onDismissError={videoCall.dismissError}
        />
      </React.Suspense>

      {/* MAP TAB */}
      <div className={`flex-1 relative ${tab === 'map' ? 'block' : 'hidden'}`}>
        <MapView
          onSelectUser={openProfile}
          onSelectPin={p => { setSelectedPin(p); setPinDetailOpen(true); }}
          onSelectEvent={e => { setSelectedEvent(e); setEventDetailOpen(true); }}
          onCreatePin={openCreatePin}
          visibilityRadius={radius}
          filters={mapFilters}
          flyTo={flyToLocation}
          onFlyToDone={() => setFlyToLocation(null)}
          lightMode={theme === 'light'}
          guestLocation={loc.lat && loc.lng ? { lat: loc.lat, lng: loc.lng } : null}
          capsules={user ? capsules.filter(c => c.creatorId === user.nullifierHash) : []}
        />

        {user && (
          <button
            onClick={() => setTab('me')}
            className="absolute top-2 left-3 z-[900] flex items-center gap-2.5 px-3 py-2 float-badge active:opacity-70 transition-opacity"
          >
            {user.avatar && <AvatarDisplay avatar={user.avatar} size={32} isMe />}
            <div className="text-left">
              <p className="text-stabeon-text text-xs font-bold leading-tight">{user.nickname}</p>
              <p className={`text-[10px] leading-tight font-medium ${loc.lat ? 'text-stabeon-online' : 'text-stabeon-muted'}`}>
                {loc.lat ? (precision === 'hidden' ? '● Hidden' : precision === 'approximate' ? '● Fuzzy' : '● Live') : 'No GPS'}
              </p>
            </div>
          </button>
        )}

        {/* Top-right: search + stats in one compact column */}
        <div className="absolute top-2 right-3 z-[900] flex flex-col gap-1.5">
          <button
            onClick={() => setSearchOpen(true)}
            className="px-3 py-2 float-badge active:opacity-70 transition-opacity flex items-center gap-1.5"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="text-stabeon-text">
              <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16" y2="16" />
            </svg>
            <span className="text-stabeon-text text-[10px] font-semibold">Search</span>
          </button>
          <div className="px-3 py-1.5 float-badge flex items-center gap-3">
            <span className="flex items-center gap-1 text-stabeon-online text-[10px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-stabeon-online dot-pulse" />
              {allUsers.filter(u => u.isOnline).length + (user ? 1 : 0)}
            </span>
            <span className="w-px h-3 bg-stabeon-border/60" />
            <span className="text-stabeon-accent text-[10px] font-bold">📍 {pins.length}</span>
            <span className="w-px h-3 bg-stabeon-border/60" />
            <span className="text-stabeon-glow text-[10px] font-bold">🎉 {allEvents.length}</span>
          </div>
        </div>

        {/* Filters below stats */}
        <div className="absolute top-[80px] right-3 z-[900]">
          <MapFilterToggle filters={mapFilters} onChange={setMapFilters} />
        </div>

        {/* Bottom hint — push higher in preview mode to not overlap guest banner */}
        <div className={`absolute left-1/2 -translate-x-1/2 z-[900] pointer-events-none ${previewMode ? 'bottom-36' : 'bottom-20'}`}>
          {pins.length === 0 ? (
            <div className="glass px-4 py-3 rounded-2xl text-center opacity-90 max-w-[260px]">
              <p className="text-stabeon-text text-xs font-semibold">Your city is quiet... for now</p>
              <p className="text-stabeon-muted text-[10px] mt-1">Long-press the map to drop a pin and start something</p>
            </div>
          ) : (
            <p className="text-stabeon-muted text-[10px] font-medium glass px-3 py-1.5 rounded-full opacity-50">
              Long-press map to drop a pin
            </p>
          )}
        </div>

        {/* Time Capsule FAB */}
        {user && (
          <button
            onClick={() => {
              const lat = loc.lat || user.lat || 0;
              const lng = loc.lng || user.lng || 0;
              openCreateCapsule(lat, lng);
            }}
            className={`absolute right-4 z-[900] flex items-center gap-2 px-3 py-2 rounded-2xl transition-all active:scale-95 ${previewMode ? 'bottom-40' : 'bottom-24'}`}
            style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.22), rgba(167,139,250,0.15))', border: '1px solid rgba(124,58,237,0.4)', backdropFilter: 'blur(12px)', boxShadow: '0 4px 16px rgba(124,58,237,0.2)' }}
            title="Drop a time capsule"
          >
            <span className="text-base">🔒</span>
            <span className="text-stabeon-accent text-[11px] font-semibold">Capsule</span>
          </button>
        )}
      </div>

      {/* EVENTS TAB */}
      {tab === 'events' && (
        <div className="flex-1 flex flex-col overflow-hidden bg-stabeon-bg tab-panel-enter md:max-w-2xl md:mx-auto md:w-full">
          <React.Suspense fallback={<div className="flex-1 flex items-center justify-center"><span className="text-stabeon-muted text-sm">Loading…</span></div>}>
          <CalendarView
            onSelectEvent={e => { setSelectedEvent(e); setEventDetailOpen(true); }}
            onCreateEvent={() => openCreateEvent()}
            onSelectUser={openProfile}
            diary={previewMode ? [] : diary}
            readOnly={previewMode}
          />
          </React.Suspense>
        </div>
      )}

      {/* PEOPLE TAB — removed; People nearby lives in Chat → ChatList */}
      {false && (() => {
        // Distance helper (km) — returns null if either point is missing
        const distKm = (u: StabeonUser): number | null => {
          if (!loc.lat || !loc.lng || !u.lat || !u.lng) return null;
          const dlat = u.lat - loc.lat;
          const dlng = (u.lng - loc.lng) * Math.cos((loc.lat * Math.PI) / 180);
          return Math.sqrt(dlat * dlat + dlng * dlng) * 111;
        };
        const fmtDist = (km: number | null): string | null => {
          if (km === null) return null;
          if (km < 1) return `${Math.round(km * 1000)} m`;
          if (km < 10) return `${km.toFixed(1)} km`;
          return `${Math.round(km)} km`;
        };
        const hasMyLoc = !!(loc.lat && loc.lng);

        // Sort by distance (nearest first) when location is available, else by lastSeen
        const sortFn = (a: StabeonUser, b: StabeonUser) => {
          if (hasMyLoc) {
            const da = distKm(a), db = distKm(b);
            if (da !== null && db !== null) return da - db;
            if (da !== null) return -1;
            if (db !== null) return 1;
          }
          return (b.lastSeen || 0) - (a.lastSeen || 0);
        };

        const onlineUsers = allUsers.filter(u => u.isOnline).sort(sortFn);
        const offlineUsers = allUsers.filter(u => !u.isOnline).sort(sortFn);
        const sortedAll = [...onlineUsers, ...offlineUsers];

        // Closest online user for hero subtitle
        const closestOnline = hasMyLoc && onlineUsers.length > 0 ? distKm(onlineUsers[0]) : null;

        return (
          <div className="flex-1 overflow-y-auto pb-24 bg-stabeon-bg tab-panel-enter md:max-w-2xl md:mx-auto md:w-full">

            {/* ── Hero header ── */}
            <div className="relative px-5 pt-5 pb-4 overflow-hidden">
              <div className="absolute -top-6 -left-6 w-48 h-48 rounded-full bg-stabeon-accent/10 blur-3xl pointer-events-none" />
              <div className="absolute -top-4 right-4 w-32 h-32 rounded-full bg-purple-400/8 blur-2xl pointer-events-none" />
              <div className="relative flex items-end justify-between">
                <div>
                  <h2 className="page-title">People Nearby</h2>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="w-2 h-2 rounded-full bg-stabeon-online dot-pulse" />
                    <span className="text-stabeon-muted text-xs">
                      {onlineUsers.length > 0
                        ? closestOnline !== null
                          ? `${onlineUsers.length} online · closest ${fmtDist(closestOnline)} away`
                          : `${onlineUsers.length} online right now`
                        : 'No one online yet'}
                    </span>
                  </div>
                </div>
                {allUsers.length > 0 && (
                  <span className="text-stabeon-muted text-xs px-2.5 py-1 rounded-full border border-stabeon-border/50">
                    {allUsers.length} total
                  </span>
                )}
              </div>
            </div>

            {allUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
                <div className="w-20 h-20 rounded-3xl bg-stabeon-accent/10 flex items-center justify-center mb-5 border border-stabeon-accent/20">
                  <span className="text-3xl">👋</span>
                </div>
                <p className="text-stabeon-text text-base font-semibold mb-1">No one here yet</p>
                <p className="text-stabeon-muted text-sm leading-relaxed">Be the first to explore the area and drop a pin on the map!</p>
              </div>
            ) : (
              <>
                {/* ── Online now — avatar strip ── */}
                {onlineUsers.length > 0 && (
                  <div className="mb-5">
                    <p className="section-label px-5 mb-3">Online now</p>
                    <div className="flex gap-4 overflow-x-auto scrollbar-hide px-5 pb-1">
                      {onlineUsers.map(u => {
                        const cat = ACTIVITY_CATEGORIES.find(c => c.key === u.activityCategory);
                        const d = fmtDist(distKm(u));
                        return (
                          <button
                            key={u.nullifierHash}
                            onClick={() => openProfile(u)}
                            className="flex flex-col items-center gap-2 flex-shrink-0 active:opacity-70 transition-opacity"
                          >
                            <div className="relative">
                              <div className="rounded-2xl ring-2 ring-stabeon-online/50 ring-offset-[3px] ring-offset-stabeon-bg overflow-hidden">
                                <AvatarDisplay avatar={u.avatar} size={52} />
                              </div>
                              {cat && (
                                <span className="absolute -bottom-1.5 -right-1.5 text-sm leading-none bg-stabeon-bg rounded-full p-0.5">
                                  {cat.emoji}
                                </span>
                              )}
                            </div>
                            <span className="text-stabeon-text text-[11px] font-medium w-[60px] text-center truncate leading-tight">
                              {u.nickname}
                            </span>
                            {d && (
                              <span className="text-stabeon-muted text-[9px] font-medium -mt-1.5 leading-none">{d}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── All people list ── */}
                <div className="px-4">
                  {offlineUsers.length > 0 && onlineUsers.length > 0 && (
                    <p className="section-label px-1 mb-3">Recently active</p>
                  )}
                  <div className="flex flex-col gap-2">
                    {sortedAll.map(u => {
                      const cat = ACTIVITY_CATEGORIES.find(c => c.key === u.activityCategory);
                      const lastSeenStr = u.lastSeen
                        ? (() => {
                            const diff = Date.now() - u.lastSeen;
                            if (diff < 60000) return 'just now';
                            if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                            if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                            return `${Math.floor(diff / 86400000)}d ago`;
                          })()
                        : 'a while ago';
                      const d = fmtDist(distKm(u));
                      return (
                        <button
                          key={u.nullifierHash}
                          onClick={() => openProfile(u)}
                          className="people-card"
                        >
                          {/* Avatar */}
                          <div className="relative flex-shrink-0">
                            <AvatarDisplay avatar={u.avatar} size={48} />
                            <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-stabeon-bg ${u.isOnline ? 'bg-stabeon-online' : 'bg-stabeon-muted/40'}`} />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-stabeon-text text-sm font-semibold truncate">{u.nickname}</span>
                              {/* World ID verified human badge */}
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 text-stabeon-accent" aria-label="Verified Human">
                                <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15"/>
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              {(u.level || 0) >= 5 && (
                                <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-stabeon-accent/15 text-stabeon-accent font-semibold leading-none">
                                  Lv{u.level}
                                </span>
                              )}
                            </div>
                            <p className="text-stabeon-muted text-[11px] mt-0.5 truncate">
                              {u.isOnline
                                ? (cat ? `${cat.emoji} ${cat.label}` : '🟢 Online now')
                                : `Last seen ${lastSeenStr}`}
                            </p>
                          </div>

                          {/* Distance + chevron */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {d && (
                              <span className="text-stabeon-muted text-[10px] font-semibold">{d}</span>
                            )}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="text-stabeon-border">
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* MATCH / CONNECT TAB */}
      {tab === 'match' && (
        <div className="flex-1 overflow-hidden bg-stabeon-bg flex flex-col tab-panel-enter md:max-w-2xl md:mx-auto md:w-full min-h-0">
          <React.Suspense fallback={<div className="flex-1 flex items-center justify-center"><span className="text-stabeon-muted text-sm">Loading…</span></div>}>
            <MatchTab
              onOpenChat={target => {
                setChatTarget(target);
                setTab('chat');
              }}
              onViewProfile={userId => {
                const u = allUsers.find(u => u.nullifierHash === userId);
                if (u) openProfile(u);
              }}
            />
          </React.Suspense>
        </div>
      )}

      {/* CHAT TAB */}
      {tab === 'chat' && (
        <div className="flex-1 overflow-hidden bg-stabeon-bg flex flex-col tab-panel-enter md:max-w-2xl md:mx-auto md:w-full min-h-0 relative">
          <React.Suspense fallback={null}>
            {agentOpen && !chatTarget ? (
              <AgentWindow onBack={() => setAgentOpen(false)} />
            ) : chatTarget ? (
              <ChatWindow
                target={chatTarget}
                onBack={() => setChatTarget(null)}
                onVideoCall={chatTarget.type === 'dm' ? handleVideoCall : undefined}
                onAudioCall={chatTarget.type === 'dm' ? handleAudioCall : undefined}
                onGroupInfo={chatTarget.type === 'group' ? () => {
                  setSelectedGroup({
                    id: chatTarget.groupId, name: chatTarget.groupName, emoji: chatTarget.groupEmoji,
                    creatorId: '', memberIds: chatTarget.memberIds, createdAt: 0,
                  });
                  setGroupInfoOpen(true);
                } : undefined}
                onMarkRead={chatTarget.type === 'dm' ? () => markConvoRead(chatTarget.peerId) : undefined}
                onMessageSent={chatTarget.type === 'dm' ? (preview: string, ts: number) => updateConvoPreview(chatTarget.peerId, preview, ts) : undefined}
              />
            ) : (
              <div className="flex-1 overflow-y-auto px-4 pb-24">
                <div className="page-header px-1">
                  <h2 className="page-title">Messages</h2>
                </div>
                <ChatList
                  onSelect={t => setChatTarget(t)}
                  onCreateGroup={() => setCreateGroupOpen(true)}
                  onOpenAgent={() => setAgentOpen(true)}
                />
              </div>
            )}
          </React.Suspense>
        </div>
      )}

      {/* OPPORTUNITIES TAB */}
      {tab === 'opportunities' && (
        <div className="flex-1 flex flex-col overflow-hidden bg-stabeon-bg tab-panel-enter md:max-w-2xl md:mx-auto md:w-full">
          <React.Suspense fallback={<div className="flex-1 flex items-center justify-center"><span className="text-stabeon-muted text-sm">Loading…</span></div>}>
            <OpportunityTab />
          </React.Suspense>
        </div>
      )}

      {/* ME TAB */}
      {tab === 'me' && (
        <div className="flex-1 overflow-y-auto px-5 pb-24 bg-stabeon-bg tab-panel-enter md:max-w-2xl md:mx-auto md:w-full">
          <div className="page-header px-0">
            <h2 className="page-title">Profile & Settings</h2>
            {isAdmin && (
              <button onClick={() => setAdminOpen(true)} className="pill-active text-xs px-3 py-1 rounded-lg ml-2">
                Dashboard
              </button>
            )}
          </div>
          {user && (() => {
            const myEventsCreated = allEventsAndPast.filter(e => e.creatorId === user.nullifierHash).length;
            const myPins = pins.filter(p => p.creatorId === user.nullifierHash).length;
            const totalReactions = allEventsAndPast
              .filter(e => e.creatorId === user.nullifierHash)
              .reduce((sum, e) => sum + (e.reactions_fire||0) + (e.reactions_heart||0) + (e.reactions_skull||0) + (e.reactions_hundred||0) + (e.reactions_eyes||0), 0);
            const statItems = [
              { icon: '🎪', label: 'Events created', value: myEventsCreated },
              { icon: '📍', label: 'Pins dropped', value: myPins },
              { icon: '⚡', label: 'Reactions earned', value: totalReactions },
              { icon: '🏅', label: 'Level', value: user.level || 1 },
            ];
            return (
              <>
                {/* ── Stats card ── */}
                <div className="mb-5 rounded-2xl overflow-hidden border border-stabeon-border/50" style={{ background: 'var(--glass-bg)' }}>
                  <div className="px-4 pt-4 pb-2 flex items-center gap-2">
                    <span className="text-base">📊</span>
                    <p className="text-stabeon-text text-sm font-semibold">Your stats</p>
                    {(user.streak || 0) > 0 && (
                      <span className="ml-auto flex items-center gap-1 text-amber-400 text-xs font-bold">
                        🔥 {user.streak}d streak
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-px bg-stabeon-border/30 border-t border-stabeon-border/30">
                    {statItems.map(({ icon, label, value }) => (
                      <div key={label} className="bg-stabeon-bg px-4 py-3">
                        <p className="text-stabeon-text text-xl font-bold leading-tight">{value}</p>
                        <p className="text-stabeon-muted text-[11px] mt-0.5">{icon} {label}</p>
                      </div>
                    ))}
                  </div>
                  {/* XP progress bar */}
                  <div className="px-4 pb-4 pt-3 border-t border-stabeon-border/30">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-stabeon-muted text-[11px]">Level {user.level || 1} · {(user.experience || 0) % 100} / 100 XP</span>
                      <span className="text-stabeon-muted text-[11px]">→ Level {(user.level || 1) + 1}</span>
                    </div>
                    <div className="w-full bg-stabeon-border rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, (user.experience || 0) % 100)}%`, background: 'linear-gradient(90deg,#7c3aed,#a78bfa)' }}
                      />
                    </div>
                  </div>
                </div>
                <SettingsPanel
                  precision={precision}
                  onPrecisionChange={p => { setPrecision(p); updateUser({ locationPrecision: p }); }}
                  radius={radius}
                  onRadiusChange={r => { setRadius(r); updateUser({ visibilityRadius: r }); }}
                  onSignOut={() => setConfirmSignOut(true)}
                  theme={theme}
                  onToggleTheme={toggleTheme}
                  notifPrefs={notifPrefs}
                  onNotifPrefsChange={updateNotifPrefs}
                />
              </>
            );
          })()}
        </div>
      )}

      <BottomNav
        active={tab}
        onChange={t => {
          if (previewMode && (t === 'chat' || t === 'me' || t === 'opportunities' || t === 'match')) {
            show('Sign in to access this feature', 'info');
            return;
          }
          if (t === 'match') setNewMatchCount(0); // clear badge on open
          setTab(t);
        }}
        onStatusTap={() => {
          if (previewMode) { show('Sign in to set your status', 'info'); return; }
          setActivityOpen(true);
        }}
        unreadCount={unreadCount}
        notifCount={notifCount}
        matchCount={newMatchCount}
      />

      {/* Preview mode banner */}
      {previewMode && (
        <div className="fixed bottom-16 left-0 right-0 z-[999] px-4 pb-2">
          <div className="relative overflow-hidden rounded-2xl max-w-lg mx-auto" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.18), rgba(167,139,250,0.12))', border: '1px solid rgba(124,58,237,0.3)', backdropFilter: 'blur(20px)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at top left, rgba(124,58,237,0.15), transparent 60%)' }} />
            <div className="relative flex items-center gap-3 px-4 py-3">
              <div className="flex-1">
                <p className="text-stabeon-text text-sm font-bold leading-tight">Exploring as guest</p>
                <p className="text-stabeon-muted text-[11px] mt-0.5">Sign in to pin, chat & join events</p>
              </div>
              <button onClick={exitPreview} className="btn-primary text-xs !py-2 !px-4 flex-shrink-0">
                Sign in →
              </button>
            </div>
          </div>
        </div>
      )}

      {suggestion && (
        <div className="fixed bottom-20 left-0 right-0 z-[1500]">
          <ActivitySuggestionCard suggestion={suggestion} onConfirm={addEntry} onDismiss={dismiss} />
        </div>
      )}

      {/* --- SHEETS --- */}
      <BottomSheet open={activityOpen} onClose={() => setActivityOpen(false)} title="Set your status">
        <ActivityPanel onClose={() => setActivityOpen(false)} />
      </BottomSheet>

      <BottomSheet open={profileOpen} onClose={() => setProfileOpen(false)}>
        {selectedUser && (
          <ProfilePanel
            user={selectedUser}
            myId={user?.nullifierHash}
            allEvents={allEventsAndPast}
            isMe={selectedUser.nullifierHash === user?.nullifierHash}
            onChat={u => {
              recordConvo(u.nullifierHash, u.nickname, u.avatar);
              setChatTarget({ type: 'dm', peerId: u.nullifierHash, peerNick: u.nickname, peerAvatar: u.avatar, peerOnline: u.isOnline, peerEpub: (u as any).epub });
              setTab('chat');
            }}
            onClose={() => setProfileOpen(false)}
            onBlockReport={openBlockReport}
          />
        )}
      </BottomSheet>

      <BottomSheet open={blockReportOpen} onClose={() => setBlockReportOpen(false)}>
        {blockReportUser && user && (
          <BlockReportSheet userId={blockReportUser.nullifierHash} userNick={blockReportUser.nickname} myId={user.nullifierHash} onClose={() => setBlockReportOpen(false)} />
        )}
      </BottomSheet>

      <BottomSheet open={createPinOpen} onClose={() => setCreatePinOpen(false)} title="Drop a pin">
        <React.Suspense fallback={null}>
          {createPinLocation && <CreatePinSheet lat={createPinLocation.lat} lng={createPinLocation.lng} onClose={() => setCreatePinOpen(false)} />}
        </React.Suspense>
      </BottomSheet>

      <BottomSheet open={pinDetailOpen} onClose={() => setPinDetailOpen(false)} fullHeight>
        <React.Suspense fallback={null}>
          {selectedPin && <PinDetailSheet pin={selectedPin} onClose={() => setPinDetailOpen(false)} />}
        </React.Suspense>
      </BottomSheet>

      <BottomSheet open={createEventOpen} onClose={() => setCreateEventOpen(false)} title="Create an event" fullHeight>
        <React.Suspense fallback={null}>
          {createEventLocation && <CreateEventSheet lat={createEventLocation.lat} lng={createEventLocation.lng} onClose={() => setCreateEventOpen(false)} />}
        </React.Suspense>
      </BottomSheet>

      <BottomSheet open={eventDetailOpen} onClose={() => setEventDetailOpen(false)} fullHeight>
        <React.Suspense fallback={null}>
          {selectedEvent && <EventDetailSheet
            event={selectedEvent}
            onClose={() => setEventDetailOpen(false)}
            onChatOrganizer={(creatorId, creatorNick) => {
              setEventDetailOpen(false);
              recordConvo(creatorId, creatorNick);
              setChatTarget({ type: 'dm', peerId: creatorId, peerNick: creatorNick, peerOnline: true });
              setTab('chat');
            }}
            onOpenEventChat={(eventId, eventTitle) => {
              setEventDetailOpen(false);
              setChatTarget({ type: 'event', eventId, eventTitle });
              setTab('chat');
            }}
          />}
        </React.Suspense>
      </BottomSheet>

      <BottomSheet open={createGroupOpen} onClose={() => setCreateGroupOpen(false)} title="Create a group">
        <React.Suspense fallback={null}>
          <CreateGroupSheet onClose={() => setCreateGroupOpen(false)} />
        </React.Suspense>
      </BottomSheet>

      <BottomSheet open={groupInfoOpen} onClose={() => setGroupInfoOpen(false)}>
        <React.Suspense fallback={null}>
          {selectedGroup && <GroupInfoSheet group={selectedGroup} onClose={() => setGroupInfoOpen(false)} />}
        </React.Suspense>
      </BottomSheet>

      {/* --- SEARCH --- */}
      {searchOpen && (
        <SearchBar
          users={allUsers}
          pins={pins}
          events={allEvents}
          onSelectUser={u => { openProfile(u); }}
          onSelectPin={p => { setSelectedPin(p); setPinDetailOpen(true); }}
          onSelectEvent={e => { setSelectedEvent(e); setEventDetailOpen(true); }}
          onGoToLocation={(lat, lng) => {
            setFlyToLocation({ lat, lng });
            setTab('map');
          }}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {/* Admin dashboard — only for admin hash */}
      {isAdmin && (
        <BottomSheet open={adminOpen} onClose={() => setAdminOpen(false)} fullHeight>
          <React.Suspense fallback={null}>
            <AdminDashboard onClose={() => setAdminOpen(false)} />
          </React.Suspense>
        </BottomSheet>
      )}

      {/* Notifications sheet — opened from Feed tab bell */}
      <BottomSheet open={notifOpen} onClose={() => setNotifOpen(false)} title="Notifications" fullHeight>
        <React.Suspense fallback={null}>
          <NotificationsPanel
            userId={user?.nullifierHash || null}
            pastEvents={pastEvents}
            onMessage={(peerId, peerNick, peerAvatar) => {
              setNotifOpen(false);
              recordConvo(peerId, peerNick, peerAvatar);
              setChatTarget({ type: 'dm', peerId, peerNick, peerAvatar, peerOnline: false });
              setTab('chat');
            }}
          />
        </React.Suspense>
      </BottomSheet>

      {/* --- CAPSULE SHEETS --- */}
      <BottomSheet open={createCapsuleOpen} onClose={() => setCreateCapsuleOpen(false)} title="Time Capsule" fullHeight>
        <React.Suspense fallback={null}>
          {createCapsuleLocation && (
            <CreateCapsuleSheet
              lat={createCapsuleLocation.lat}
              lng={createCapsuleLocation.lng}
              onClose={() => setCreateCapsuleOpen(false)}
              onCreated={() => setCreateCapsuleOpen(false)}
            />
          )}
        </React.Suspense>
      </BottomSheet>

      {/* Capsule reveal — full-screen overlay outside BottomSheet */}
      {revealingCapsule && (
        <React.Suspense fallback={null}>
          <CapsuleRevealSheet
            capsule={revealingCapsule}
            onClose={() => {
              markRevealed(revealingCapsule.id);
              const next = revealQueueRef.current.shift() || null;
              setRevealingCapsule(next);
            }}
            onDropAnother={() => {
              markRevealed(revealingCapsule.id);
              setRevealingCapsule(null);
              setCreateCapsuleLocation({ lat: revealingCapsule.lat, lng: revealingCapsule.lng });
              setCreateCapsuleOpen(true);
            }}
          />
        </React.Suspense>
      )}

      {/* --- CONFIRM DIALOGS --- */}
      <ConfirmDialog
        open={confirmSignOut}
        title="Sign out?"
        message="You'll need to verify with WorldID again to sign back in."
        confirmLabel="Sign out"
        danger
        onConfirm={() => { setConfirmSignOut(false); logout(); }}
        onCancel={() => setConfirmSignOut(false)}
      />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <MiniKitProvider>
          <GunProvider>
            <UserProvider>
              <AppInner />
            </UserProvider>
          </GunProvider>
        </MiniKitProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
